import { Box, Button, Grid2 as Grid, Link, Stack } from '@mui/material';
import React, { FC, useMemo } from 'react';

import { AbgTypography } from '@abg/kit-base';
import { OpeningHours } from '@abg/typings';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { formatHours } from '../../../../organisms/LocationMap/utils/operation-hours.util';
import { formatPageLocaleToISOCode } from '../../../../shared/utils';
import { EmptyState } from '../../atoms/EmptyState';
import { LocationIcon } from '../../atoms/location-icon/LocationIcon';
import { LocationFeatures } from '../location-features/LocationFeatures';

import { useLocationPageUrl } from './hooks/use-location-page-url';
import { LocationDetailsProperties } from './location-details.types';
import * as styles from './LocationDetails.styling';
import { getLocationDetailsAddress } from './utils';

export const LocationDetails: FC<LocationDetailsProperties> = ({
  option,
  uiTextData,
  locationType,
}) => {
  const locationUrl = useLocationPageUrl(option);
  const { locale, firstDayWeek, timeFormat } = usePageContext();
  const hourOfOperation = useMemo(() => {
    if (option.hoursOfOperation) {
      const schedule = option.hoursOfOperation.filter(
        (element) => element.dayOfWeek !== null,
      ) as Array<OpeningHours>;

      return formatHours(
        schedule,
        true,
        formatPageLocaleToISOCode(locale),
        firstDayWeek,
        timeFormat,
      );
    }

    return [];
  }, [firstDayWeek, locale, option.hoursOfOperation, timeFormat]);

  const locationModel = useMemo(
    () => ({
      title: option.locationName,
      locationFeatures: {
        is24hoursDropOffAvailable: option?.is24hoursDropOffAvailable ?? false,
        isKeyDropLocation: option?.isKeyDropLocation ?? false,
      },
      address: getLocationDetailsAddress(
        option,
        uiTextData?.multipleLocationsLabel,
      ),
      hourOfOperation,
      phones: option.phones ?? [],
      locationUrl,
    }),
    [option, hourOfOperation, locationUrl],
  );

  return (
    <Box
      sx={styles.locationDetailContainer}
      data-testid="location-details-root"
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={styles.iconContainer}
          data-testid="location-details-header-icon"
        >
          <LocationIcon location={option} mode="detail" />
        </Box>
        <AbgTypography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          noWrap
          sx={styles.h5}
          title={locationModel.title}
          data-testid="location-details-header-title"
        >
          {locationModel.title}
        </AbgTypography>
      </Stack>

      <LocationFeatures
        features={locationModel.locationFeatures}
        featureTranslations={{
          is24hoursDropOffAvailable:
            uiTextData?.is24hoursDropOffAvailableLabel || '',
          isKeyDropLocation: uiTextData?.isKeyDropLocationLabel || '',
        }}
        dataTestId="location-details-location-features-container"
        siteCodeGroup={option.siteCodeGroup ?? undefined}
        isAvisFirstLocation={option.isAvisFirstLocation}
        isAvisFirstLiteLocation={option.isAvisFirstLite}
        deliveryLabels={{
          'avisfirst.lite.delivery.label':
            uiTextData?.avisFirstLiteDeliveryLabel,
          'avisfirst.curbside.delivery.label':
            uiTextData?.avisFirstCurbsideDeliveryLabel ?? '',
          'avisfirst.athome.delivery.label':
            uiTextData?.avisFirstAtHomeDeliveryLabel ?? '',
        }}
        locationType={locationType}
      />

      <Grid
        mt={3}
        container
        direction="row"
        justifyContent="space-between"
        columnSpacing={2}
        rowSpacing={1.7}
      >
        {/* Address */}
        <Grid size={5}>
          <Stack>
            <AbgTypography
              sx={styles.subHeading}
              data-testid="location-details-address-label"
            >
              {uiTextData?.addressLabel}
            </AbgTypography>

            <Stack>
              {locationModel.address.length > 0 ? (
                locationModel.address.map((address, index) => (
                  <AbgTypography
                    key={index}
                    sx={styles.typographyAddressSubText}
                    data-testid={`location-details-address-row-${index}`}
                  >
                    {address}
                  </AbgTypography>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        </Grid>

        {/* Hours of Operation */}
        <Grid size={7} pl={1.2}>
          <AbgTypography
            sx={styles.subHeading}
            data-testid="location-details-hoursOfOperation-label"
          >
            {uiTextData?.hoursOfOperationLabel}
          </AbgTypography>

          <Stack>
            {locationModel.hourOfOperation.length > 0 ? (
              locationModel.hourOfOperation.map((entry, dayIndex) => (
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 2fr"
                  gap={0.5}
                  key={`${entry.days}-${dayIndex}`}
                  data-testid={`location-details-hoursOfOperation-dayRow-${dayIndex}`}
                >
                  <AbgTypography
                    sx={styles.typographyHoursSubText}
                    data-testid={`hoursOfOperation-dayRow-${dayIndex}-label`}
                  >
                    {entry.days}
                  </AbgTypography>
                  <Box
                    flexDirection="column"
                    data-testid={`hoursOfOperation-dayRow-${dayIndex}-values`}
                  >
                    {entry.schedule.map((time, timeIndex) => (
                      <AbgTypography
                        key={`${time}-${timeIndex}`}
                        sx={styles.typographyHoursSubText}
                        data-testid={`hoursOfOperation-dayRow-${dayIndex}-timeRow-${timeIndex}`}
                      >
                        {time}
                      </AbgTypography>
                    ))}
                  </Box>
                </Box>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Grid>

        {/* Phone */}
        <Grid size={5}>
          <Stack>
            <AbgTypography
              sx={styles.subHeading}
              data-testid="location-details-phone-label"
            >
              {uiTextData?.phoneLabel}
            </AbgTypography>
            {locationModel.phones?.length ? (
              locationModel.phones.map((phone, index) => (
                <AbgTypography
                  key={`${phone}-${index}`}
                  sx={styles.typographyAddressSubText}
                  data-testid={`location-details-phone-row-${index}`}
                >
                  {phone}
                </AbgTypography>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Grid>

        {/* Location Details Link */}
        <Grid size={7}>
          {option.relPath && option.regionCode && locationModel.locationUrl && (
            <Box
              sx={styles.linkContainer}
              data-testid="location-details-link-container"
            >
              <Button
                href={locationModel.locationUrl}
                component={Link}
                variant="text"
                data-testid="location-details-link"
                target="_blank"
                className="secondaryText"
              >
                {uiTextData?.locationLinkLabel}
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
